/**
 * The only place that builds a document base (docs/large-volume-plan.md §2.1, ADR 0003).
 *
 * The document base is always a `ChunkedVoxelGrid`. Every construction site of a durable base
 * (document build, checkpoint decode, log hydration, reducer `setBase`, new documents) calls
 * `createDocumentBase` or `toDocumentBase`, so the representation is decided here and nowhere else.
 * Native `Map`s remain the sparse representation for layers, previews and tests; they are converted
 * here when they become a base.
 *
 * Palette lineage: a grid carries its palette; `toDocumentBase(grid, palette)` rebases a grid built
 * over a foreign palette (for example a decoded checkpoint) onto the lineage palette.
 */
import type { Position } from '@/voxelMath'
import type { VoxelData, VoxelMap } from '@/voxelTypes'
import { ChunkedVoxelGrid, isChunkedVoxelGrid } from './ChunkedVoxelGrid'
import { VoxelPalette } from './palette'
import { LINEAR_TO_MORTON12 } from './layout'

type EntrySource = Iterable<[number, VoxelData]> | VoxelMap

function forEachEntry(source: EntrySource, fn: (key: number, value: VoxelData) => void): void {
  const maybe = source as { forEach?: unknown; get?: unknown }
  // Map-style `forEach((value, key))` only for map-likes; arrays of [key, value] tuples also have a
  // `forEach`, but with (entry, index) arguments, so they take the iterator path below.
  if (!Array.isArray(source) && typeof maybe.forEach === 'function' && typeof maybe.get === 'function') {
    ;(source as { forEach: (cb: (v: VoxelData, k: number) => void) => void }).forEach((v, k) => fn(k, v))
    return
  }
  for (const [k, v] of source as Iterable<[number, VoxelData]>) fn(k, v)
}

function buildGrid(source: EntrySource | undefined, palette: VoxelPalette, bounds?: Readonly<Position> | null): ChunkedVoxelGrid {
  const g = new ChunkedVoxelGrid(palette, bounds).asMutable()
  if (source) {
    if (isChunkedVoxelGrid(source) && source.palette === palette) {
      // Same palette: copy ids without re-interning.
      source.forEachChunk((chunkId, chunk) => {
        if (chunk.count === 0) return
        const d = chunk.data
        for (let i = 0; i < 4096; i++) if (d[i] !== 0) g.setId((chunkId << 12) | LINEAR_TO_MORTON12[i], d[i])
      })
    } else {
      forEachEntry(source, (k, v) => { g.set(k, v) })
    }
  }
  return g.asImmutable()
}

/**
 * Creates a frozen document base from optional entries. The palette (new unless given) and bounds
 * (whole domain unless given) are attached to the grid. A grid on the requested palette is returned
 * as is.
 */
export function createDocumentBase(
  entries?: EntrySource,
  palette?: VoxelPalette,
  bounds?: Readonly<Position> | null,
): ChunkedVoxelGrid {
  if (entries && isChunkedVoxelGrid(entries) && (!palette || entries.palette === palette)) return entries
  return buildGrid(entries, palette ?? new VoxelPalette(), bounds)
}

/**
 * Normalizes any `VoxelMap` to a document base. Grids already on `palette` (or on any palette when
 * none is given) are returned as is; grids on a foreign palette are rebased; native Maps are copied
 * into a new grid over `bounds`.
 */
export function toDocumentBase(
  m: VoxelMap | undefined | null,
  palette?: VoxelPalette,
  bounds?: Readonly<Position> | null,
): ChunkedVoxelGrid {
  if (!m) return createDocumentBase(undefined, palette, bounds)
  if (isChunkedVoxelGrid(m)) {
    if (!palette || m.palette === palette) return m
    return buildGrid(m, palette, bounds ?? m.bounds)
  }
  return buildGrid(m, palette ?? new VoxelPalette(), bounds)
}

/**
 * Returns `grid` when its bounds already equal `bounds`, otherwise a copy of it clipped to `bounds`
 * on the same palette. Used where a base and its document bounds are reunited after the fact (a
 * revived legacy checkpoint; see persistence/devalueCodec.ts).
 */
export function withDocumentBounds(grid: ChunkedVoxelGrid, bounds: Readonly<Position> | null | undefined): ChunkedVoxelGrid {
  if (!bounds) return grid
  const b = grid.bounds
  if (b.x === bounds.x && b.y === bounds.y && b.z === bounds.z) return grid
  return buildGrid(grid, grid.palette, bounds)
}

/** The lineage palette of a base, if it is a grid. */
export function documentPaletteOf(m: VoxelMap | undefined | null): VoxelPalette | undefined {
  return m && isChunkedVoxelGrid(m) ? m.palette : undefined
}
